/**
 * Per-module permission helpers.
 *
 * Mirrors the backend's naming CONVENTION (`module.resource.action`), not its
 * data. The authoritative list lives in `tudonum_backend/rbac/modules/` and is
 * served to the UI by `GET /rbac/permissions/`; this file exists so call sites
 * get a typed, autocompleted builder instead of ~45 hand-written strings.
 *
 * `defineModule` makes a typo a COMPILE error: `can("restaurant", "aprove")`
 * fails `tsc`, which is the closest the frontend can get to registry validation
 * without a round-trip.
 */

type ActionsOf<T extends Record<string, readonly string[]>> = {
  [R in keyof T]: T[R][number];
};

export interface PermissionModule<T extends Record<string, readonly string[]>> {
  key: string;
  resources: T;
  can<R extends keyof T & string>(resource: R, action: ActionsOf<T>[R]): string;
}

export function defineModule<const T extends Record<string, readonly string[]>>(
  moduleKey: string,
  resources: T,
): PermissionModule<T> {
  return {
    key: moduleKey,
    resources,
    /** Build a codename: can("restaurant", "approve") -> "food.restaurant.approve" */
    can<R extends keyof T & string>(resource: R, action: ActionsOf<T>[R]): string {
      return `${moduleKey}.${resource}.${action}`;
    },
  };
}

/**
 * Food Delivery. Keep in step with `tudonum_backend/rbac/modules/food.py`; the
 * Permissions Catalogue is the source of truth for what actually exists.
 */
export const FOOD = defineModule("food", {
  dashboard: ["view"],
  restaurant: [
    "view", "create", "edit", "export",
    "approve", "reject", "suspend", "reactivate",
    "revert_to_pending", "operations_activate", "approval_history", "review",
  ],
  restaurant_document: ["view", "download", "review", "approve", "reject", "history"],
  restaurant_compliance: ["view", "verify", "revert"],
  business_type: ["view", "create", "edit", "deactivate", "catalog_toggle"],
  cuisine_type: ["view", "create", "edit", "deactivate", "catalog_toggle"],
  service_mode: ["view", "create", "edit", "deactivate", "catalog_toggle"],
  item_category: ["view", "create", "edit", "deactivate", "catalog_toggle"],
  document_type: ["view", "create", "edit", "deactivate", "catalog_toggle"],
  country_rule: ["view", "edit"],
  city_rule: ["view", "edit"],
  document_rule: ["view", "edit", "bulk_apply"],
  profile_rule: ["view", "edit"],
  order: ["view", "cancel"],
  rider: ["view", "manage"],
  deal: ["view", "toggle"],
  delivery_fee: ["view", "edit"],
  bulk: ["view", "approve", "reject", "suspend", "reactivate", "catalog_toggle", "export"],
  audit: ["view", "export"],
} as const);

/**
 * The Food navigation surface: ONE definition consumed by both the router
 * (`App.tsx`) and the sidebar (`sidebar-config.ts`).
 *
 * These two files previously carried the same 8 permission strings as
 * independent literals, kept in step by hand -- which is how
 * `/food/country-catalog` ended up gated on a codename its backend never
 * accepted, producing a guaranteed 403 on every request.
 */
export interface ModuleNavEntry {
  id: string;
  label: string;
  path: string;
  permission: string;
}

export const FOOD_NAV: readonly ModuleNavEntry[] = [
  { id: "food-dashboard", label: "Dashboard", path: "/food/dashboard", permission: FOOD.can("dashboard", "view") },
  { id: "food-restaurants", label: "Restaurants", path: "/food/restaurants", permission: FOOD.can("restaurant", "view") },
  { id: "food-orders", label: "Orders", path: "/food/orders", permission: FOOD.can("order", "view") },
  { id: "food-riders", label: "Riders", path: "/food/riders", permission: FOOD.can("rider", "view") },
  { id: "food-delivery-fee", label: "Delivery Fee", path: "/food/delivery-fee", permission: FOOD.can("delivery_fee", "view") },
  { id: "food-deals", label: "Deals", path: "/food/deals", permission: FOOD.can("deal", "view") },
  { id: "food-country-catalog", label: "Restaurant Catalog", path: "/food/country-catalog", permission: FOOD.can("country_rule", "edit") },
] as const;

/** Route path -> permission, for App.tsx. */
export const FOOD_ROUTE_PERMISSIONS: Record<string, string> = Object.fromEntries(
  FOOD_NAV.map((e) => [e.path, e.permission]),
);

/**
 * Tudo Social. Keep in step with `tudonum_backend/rbac/modules/tudo_social.py`.
 *
 * The split that matters here: `article.edit`, `article.publish` and
 * `article_region.manage` are three separate authorities. Writing an article,
 * deciding it is fit to publish, and deciding which countries see it are
 * different decisions -- the last is commercial, and on a surface with regional
 * pricing and regional law it is the one worth keeping out of a writer's hands.
 */
export const TUDO_SOCIAL = defineModule("tudo_social", {
  article: ["view", "create", "edit", "publish", "delete"],
  post: ["view", "moderate", "delete", "pin"],
  comment: ["view", "moderate"],
  report: ["view", "resolve"],
  channel: ["view", "approve", "reject", "suspend", "create_official"],
  channel_document: ["view", "download", "approve", "reject"],
  video: ["view", "moderate", "delete", "feature"],
  game: ["view", "manage"],
  game_outcome: ["view", "manage"],
  game_reward_rule: ["view", "manage"],
  game_play: ["view", "resolve"],
  tv_document_rule: ["view", "manage"],
  article_region: ["view", "manage"],
  space: ["view", "manage"],
  space_region: ["view", "manage"],
  suggestion: ["view", "resolve"],
} as const);

/**
 * Third-party suppliers — Tudo eSIM, Tudo Digital Gift Cards and Tudo Travel.
 *
 * Nothing here is approved or onboarded: every row is a purchase already
 * made with Duffel, Zendit or Airalo. The authorities split by how dangerous
 * the action is, not by which product it touches — `reconcile` asks the
 * supplier and records their answer, `resolve` writes an outcome nobody
 * confirmed.
 */
export const THIRD_PARTY = defineModule("third_party", {
  order: ["view", "reconcile", "resolve"],
  supplier: ["view"],
} as const);

export const THIRD_PARTY_NAV: readonly ModuleNavEntry[] = [
  { id: "third-party-orders", label: "Supplier Orders", path: "/third-party/orders", permission: THIRD_PARTY.can("order", "view") },
] as const;

/** Route path -> permission, for App.tsx. */
export const THIRD_PARTY_ROUTE_PERMISSIONS: Record<string, string> = Object.fromEntries(
  THIRD_PARTY_NAV.map((e) => [e.path, e.permission]),
);

export const TUDO_SOCIAL_NAV: readonly ModuleNavEntry[] = [
  { id: "tudo-social-articles", label: "Articles", path: "/tudo-social/articles", permission: TUDO_SOCIAL.can("article", "view") },
  { id: "tudo-social-suggestions", label: "Reader Suggestions", path: "/tudo-social/suggestions", permission: TUDO_SOCIAL.can("suggestion", "view") },
  { id: "tudo-social-moderation", label: "Posts & Reports", path: "/tudo-social/moderation", permission: TUDO_SOCIAL.can("post", "view") },
  { id: "tudo-social-videos", label: "Videos", path: "/tudo-social/videos", permission: TUDO_SOCIAL.can("video", "view") },
  { id: "tudo-social-channels", label: "Channels", path: "/tudo-social/channels", permission: TUDO_SOCIAL.can("channel", "view") },
  { id: "tudo-social-tv-rules", label: "Required Documents", path: "/tudo-social/tv-documents", permission: TUDO_SOCIAL.can("tv_document_rule", "view") },
  { id: "tudo-social-games", label: "Games", path: "/tudo-social/games", permission: TUDO_SOCIAL.can("game", "view") },
  { id: "tudo-social-game-plays", label: "Game Plays", path: "/tudo-social/game-plays", permission: TUDO_SOCIAL.can("game_play", "view") },
  { id: "tudo-social-rewards", label: "Rewards", path: "/tudo-social/rewards", permission: TUDO_SOCIAL.can("game_reward_rule", "view") },
  // One page, three routes. Categories are ONE table discriminated by
  // surface, so they share a component — but each product needs its own path:
  // `findNavMatch` matches on pathname alone, so a `?surface=` deep link would
  // never highlight, and filing rooms only under Setup is why nobody could
  // find them.
  { id: "tudo-social-categories", label: "Categories", path: "/tudo-social/categories", permission: TUDO_SOCIAL.can("space", "view") },
  { id: "tudo-social-rooms", label: "Rooms", path: "/tudo-social/rooms", permission: TUDO_SOCIAL.can("space", "view") },
  { id: "tudo-social-tv-categories", label: "Categories", path: "/tudo-social/tv-categories", permission: TUDO_SOCIAL.can("space", "view") },
  { id: "tudo-social-availability", label: "Customer Availability", path: "/tudo-social/availability", permission: TUDO_SOCIAL.can("space_region", "view") },
  { id: "tudo-social-onboarding", label: "Channel Onboarding", path: "/tudo-social/onboarding", permission: TUDO_SOCIAL.can("space_region", "view") },
] as const;

/** Route path -> permission, for App.tsx. */
export const TUDO_SOCIAL_ROUTE_PERMISSIONS: Record<string, string> = Object.fromEntries(
  TUDO_SOCIAL_NAV.map((e) => [e.path, e.permission]),
);
